import http from "@/helper/http";
import api from "@/helper/api";

// EBM settings are managed by rraEbmSettings; this module keeps store metadata hydrated
export default {
  state: {
    loadingStores: false,
    stores: [],
    creatingMerchantCode: false,
  },
  mutations: {
    SET_LOADING_STORE(state, loadingStores) {
      state.loadingStores = loadingStores;
    },
    SET_STORES(state, stores) {
      state.stores = stores;
    },
    SET_CREATING_MERCHANT_CODE(state, status) {
      state.creatingMerchantCode = status;
    },
    UPDATE_STORE_MERCHANT_CODE(state, { storeId, merchantCode }) {
      if (!storeId) {
        return;
      }

      const normalizedId = String(storeId);
      const updatedStores = state.stores.map((store) => {
        if (!store?.id || String(store.id) !== normalizedId) {
          return store;
        }

        return {
          ...store,
          merchant_code: merchantCode,
        };
      });

      state.stores = updatedStores;
      localStorage.setItem("stores", JSON.stringify(updatedStores));
    },
  },
  actions: {
    async createStore(context, storeData) {
      try {
        const response = await http.post(api.store_v1, storeData);

        if (response?.data) {
          // Return the created store
          return response.data;
        }

        throw new Error("Failed to create store");
      } catch (error) {
        console.error("Error creating store:", error);
        throw error;
      }
    },
    async getAllUsersStore({ commit }) {
      commit("SET_LOADING_STORE", true);
      try {
        const response = await http.get(api.get_user_stores);
        const storesPayload = response?.data ?? {};

        const permissionsByStoreId = new Map();
        const memberEntries = storesPayload?.member_of ?? [];

        memberEntries.forEach((membership) => {
          const store = membership?.store;
          if (!store?.id) {
            return;
          }

          const storeId = store.id;
          const permissionRecord = membership?.permissions ?? {};
          const permissionList = Array.isArray(permissionRecord?.permissions)
            ? permissionRecord.permissions
            : [];

          const existingEntry = permissionsByStoreId.get(storeId) ?? {
            list: [],
            grants: [],
          };

          const mergedPermissions = new Set([
            ...(existingEntry.list ?? []),
            ...permissionList,
          ]);

          permissionsByStoreId.set(storeId, {
            list: Array.from(mergedPermissions),
            grants: [...(existingEntry.grants ?? []), permissionRecord],
          });
        });

        const storeMemberships =
          storesPayload?.member_of?.map((membership) => ({
            ...membership.store,
            isOwner: false,
            membershipType: "member",
            permissions:
              permissionsByStoreId.get(membership?.store?.id)?.list ?? [],
            permissionGrants:
              permissionsByStoreId.get(membership?.store?.id)?.grants ?? [],
          })) ?? [];

        const ownerStores =
          storesPayload?.owner_of?.map((store) => ({
            ...store,
            isOwner: true,
            membershipType: "owner",
            permissions: permissionsByStoreId.get(store?.id)?.list ?? [
              "OWNER_FULL_ACCESS",
            ],
            permissionGrants: permissionsByStoreId.get(store?.id)?.grants ?? [],
          })) ?? [];

        const jackAccessStores =
          storesPayload?.jack_access_of?.map((entry) => ({
            ...entry.store,
            isOwner: false,
            membershipType: "jack_access",
            jackAccessId: entry.id,
            jackAccessExpiresAt: entry.expires_at ?? null,
            jackAccessGrantedAt: entry.created_at ?? null,
            permissions: permissionsByStoreId.get(entry?.store?.id)?.list ?? [],
            permissionGrants:
              permissionsByStoreId.get(entry?.store?.id)?.grants ?? [],
          })) ?? [];

        const combinedStores = [
          ...ownerStores,
          ...storeMemberships,
          ...jackAccessStores,
        ];

        const uniqueStores = [];
        const seenStoreIds = new Set();

        combinedStores.forEach((store) => {
          if (!store?.id) {
            return;
          }

          const storeId = store.id;
          if (seenStoreIds.has(storeId)) {
            return;
          }

          seenStoreIds.add(storeId);

          const permissionsMeta = permissionsByStoreId.get(storeId) ?? {
            list: [],
            grants: [],
          };

          uniqueStores.push({
            ...store,
            permissions: permissionsMeta.list ?? [],
            permissionGrants: permissionsMeta.grants ?? [],
          });
        });

        const initialStores = uniqueStores.map((store) => ({
          ...store,
          // EBM settings are managed separately by rraEbmSettings
        }));

        localStorage.setItem("stores", JSON.stringify(initialStores));
        commit("SET_STORES", initialStores);

        return {
          stores: initialStores,
          raw: storesPayload,
        };
      } catch (error) {
        console.error("Failed to fetch user stores:", error);

        try {
          const localStores = JSON.parse(
            localStorage.getItem("stores") || "[]"
          );
          if (localStores && localStores.length > 0) {
            commit("SET_STORES", localStores);
            return {
              stores: localStores,
              raw: {},
            };
          }
        } catch (localError) {
          console.error("Failed to parse localStorage stores:", localError);
        }

        commit("SET_STORES", []);
        return {
          stores: [],
          raw: {},
        };
      } finally {
        commit("SET_LOADING_STORE", false);
      }
    },
    async createStoreMerchantCode({ commit, rootState, state }, storeId) {
      if (!storeId) {
        throw new Error("A store ID is required to generate a merchant code");
      }

      commit("SET_CREATING_MERCHANT_CODE", true);
      try {
        const response = await http.post(
          api.generate_store_merchant_code(storeId),
          {
            store_id: storeId,
          }
        );
        const merchantCode = response?.data?.merchant_code ?? null;

        if (merchantCode) {
          commit("UPDATE_STORE_MERCHANT_CODE", {
            storeId,
            merchantCode,
          });

          const selectedStore = rootState?.dashboard?.selectedStore;
          const selectedStoreId =
            typeof selectedStore === "object" && selectedStore !== null
              ? selectedStore.id
              : selectedStore;

          if (selectedStoreId && String(selectedStoreId) === String(storeId)) {
            const updatedStore =
              state.stores.find(
                (store) => String(store?.id) === String(storeId)
              ) ?? null;

            commit("SET_SELECTED_STORE", updatedStore ?? selectedStoreId, {
              root: true,
            });
          }
        }

        return response?.data;
      } catch (error) {
        console.error("Failed to generate merchant code:", error);
        throw error;
      } finally {
        commit("SET_CREATING_MERCHANT_CODE", false);
      }
    },
  },
  getters: {
    isLoadingStores: (state) => state.loadingStores,
    getUserStores: (state) => state.stores,
    isCreatingMerchantCode: (state) => state.creatingMerchantCode,
    getStoreById: (state) => (storeId) => {
      if (!storeId) {
        return null;
      }

      const normalizedId = String(storeId);
      return (
        state.stores.find((store) => String(store?.id) === normalizedId) ?? null
      );
    },
  },
};
